import { useState } from "react";
import { Folder as FolderIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { fileExtension, formatFileDate, formatFileSize } from "@/lib/files-format";
import { FileActionsMenu } from "./FileActionsMenu";
import { DRAG_ITEM_MIME, fileIconFor, type DraggedItem, type FileRow, type FolderRow } from "./files-types";

type FixedEntry = { key: string; label: string; icon: typeof FolderIcon; onClick: () => void };

type Actions = {
  onOpenFolder: (folder: FolderRow) => void;
  onOpenFile: (file: FileRow) => void;
  onRenameFolder: (folder: FolderRow) => void;
  onMoveFolder: (folder: FolderRow) => void;
  onDeleteFolder: (folder: FolderRow) => void;
  onDownloadFile: (file: FileRow) => void;
  onTagsFile: (file: FileRow) => void;
  onShareFile: (file: FileRow) => void;
  onHistoryFile: (file: FileRow) => void;
  onRenameFile: (file: FileRow) => void;
  onMoveFile: (file: FileRow) => void;
  onDeleteFile: (file: FileRow) => void;
  onDropItemOnFolder?: (item: DraggedItem, folder: FolderRow) => void;
  onDropFilesOnFolder?: (folder: FolderRow, files: FileList) => void;
};

export function FilesTable({
  fixedEntries,
  folders,
  files,
  actions,
}: {
  fixedEntries?: FixedEntry[];
  folders: FolderRow[];
  files: FileRow[];
  actions: Actions;
}) {
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  function handleFolderDragOver(e: React.DragEvent, folderId: string) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(folderId);
  }

  function handleFolderDrop(e: React.DragEvent, folder: FolderRow) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
    if (e.dataTransfer.types.includes("Files")) {
      if (e.dataTransfer.files.length > 0) actions.onDropFilesOnFolder?.(folder, e.dataTransfer.files);
      return;
    }
    const raw = e.dataTransfer.getData(DRAG_ITEM_MIME);
    if (!raw) return;
    const item = JSON.parse(raw) as DraggedItem;
    if (item.kind === "pasta" && item.id === folder.id) return;
    actions.onDropItemOnFolder?.(item, folder);
  }

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-[46%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[18%]" />
          <col className="w-[8%]" />
        </colgroup>
        <thead className="border-b border-zinc-100 bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-500 dark:border-border dark:bg-background/50 dark:text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Nome</th>
            <th className="px-2 py-2 text-left font-semibold">Tipo</th>
            <th className="px-2 py-2 text-left font-semibold">Tamanho</th>
            <th className="px-2 py-2 text-left font-semibold">Data</th>
            <th className="px-2 py-2 text-center font-semibold">Ações</th>
          </tr>
        </thead>
        <tbody>
          {fixedEntries?.map((entry) => (
            <tr
              key={entry.key}
              onClick={entry.onClick}
              className="cursor-pointer border-b border-zinc-100 hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-white/5"
            >
              <td className="flex items-center gap-2 px-3 py-2 font-medium text-zinc-900 dark:text-foreground">
                <entry.icon className="h-4 w-4 shrink-0 text-brand-500 dark:text-primary" />
                {entry.label}
              </td>
              <td className="px-2 py-2 text-zinc-400 dark:text-muted-foreground">—</td>
              <td className="px-2 py-2 text-zinc-400 dark:text-muted-foreground">—</td>
              <td className="px-2 py-2 text-zinc-400 dark:text-muted-foreground">—</td>
              <td className="px-2 py-2" />
            </tr>
          ))}

          {folders.map((folder) => (
            <tr
              key={folder.id}
              onClick={() => actions.onOpenFolder(folder)}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(DRAG_ITEM_MIME, JSON.stringify({ kind: "pasta", id: folder.id, name: folder.name }));
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => handleFolderDragOver(e, folder.id)}
              onDragLeave={() => setDragOverFolderId((prev) => (prev === folder.id ? null : prev))}
              onDrop={(e) => handleFolderDrop(e, folder)}
              className={cn(
                "cursor-pointer border-b border-zinc-100 dark:border-border/50",
                dragOverFolderId === folder.id
                  ? "bg-brand-50 dark:bg-primary/10"
                  : "hover:bg-zinc-50 dark:hover:bg-white/5",
              )}
            >
              <td className="truncate px-3 py-2 font-medium text-zinc-900 dark:text-foreground">
                <span className="flex items-center gap-2">
                  <FolderIcon className="h-4 w-4 shrink-0 fill-amber-100 text-amber-500 dark:fill-amber-500/20 dark:text-amber-400" />
                  <span className="truncate">{folder.name}</span>
                </span>
              </td>
              <td className="px-2 py-2 text-zinc-500 dark:text-muted-foreground">Pasta</td>
              <td className="px-2 py-2 text-zinc-400 dark:text-muted-foreground">—</td>
              <td className="px-2 py-2 text-zinc-400 dark:text-muted-foreground">—</td>
              <td className="px-2 py-2">
                <div className="flex justify-center">
                  <FileActionsMenu
                    onRename={() => actions.onRenameFolder(folder)}
                    onMove={() => actions.onMoveFolder(folder)}
                    onDelete={() => actions.onDeleteFolder(folder)}
                  />
                </div>
              </td>
            </tr>
          ))}

          {files.map((file) => {
            const Icon = fileIconFor(file.contentType);
            return (
              <tr
                key={file.id}
                onClick={() => actions.onOpenFile(file)}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_ITEM_MIME, JSON.stringify({ kind: "arquivo", id: file.id, name: file.name }));
                  e.dataTransfer.effectAllowed = "move";
                }}
                className="cursor-pointer border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-white/5"
              >
                <td className="truncate px-3 py-2 font-medium text-zinc-900 dark:text-foreground" title={file.name}>
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-zinc-400 dark:text-muted-foreground" />
                    <span className="truncate">{file.name}</span>
                    {file.tags && file.tags.length > 0 && (
                      <span className="shrink-0 truncate text-[11px] font-normal text-brand-500 dark:text-primary">
                        {file.tags.map((t) => t.tag.name).join(", ")}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-2 py-2 text-zinc-500 dark:text-muted-foreground">{fileExtension(file.name)}</td>
                <td className="px-2 py-2 tabular-nums text-zinc-500 dark:text-muted-foreground">
                  {formatFileSize(file.size)}
                </td>
                <td className="px-2 py-2 text-zinc-500 dark:text-muted-foreground">{formatFileDate(file.createdAt)}</td>
                <td className="px-2 py-2">
                  <div className="flex justify-center">
                    <FileActionsMenu
                      onDownload={() => actions.onDownloadFile(file)}
                      onTags={() => actions.onTagsFile(file)}
                      onShare={() => actions.onShareFile(file)}
                      onHistory={() => actions.onHistoryFile(file)}
                      onRename={() => actions.onRenameFile(file)}
                      onMove={() => actions.onMoveFile(file)}
                      onDelete={() => actions.onDeleteFile(file)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
